import React, { useState, useRef, useEffect } from 'react';
import { X, Save, RefreshCw, Clock, Repeat, GripHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import CustomDropdown from '../ui/CustomDropdown';

// Availability subtypes - all use green color
const AVAILABILITY_TYPES = [
    { value: 'available', labelFr: 'Disponible', labelEn: 'Available' },
    { value: 'phone', labelFr: 'Permanence téléphonique', labelEn: 'Phone availability' },
    { value: 'consultation', labelFr: 'Consultation', labelEn: 'Consultation' },
    { value: 'remote', labelFr: 'Télétravail', labelEn: 'Remote work' },
    { value: 'onsite', labelFr: 'Sur place', labelEn: 'On site' },
    { value: 'video', labelFr: 'Visioconférence', labelEn: 'Video call' },
    { value: 'on_request', labelFr: 'Sur demande', labelEn: 'On request' },
];

/**
 * Availability Configuration Modal
 * Mini weekly calendar to set availability slots
 */
const AvailabilityModal = ({ isOpen, onClose, onSave, existingEvents = [] }) => {
    const { t, language } = useTranslation();
    const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);
    const [currentSelection, setCurrentSelection] = useState(null);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [pendingSlot, setPendingSlot] = useState(null);
    const [slotType, setSlotType] = useState('available');
    const [slotTitle, setSlotTitle] = useState('');
    const [slotRecurrent, setSlotRecurrent] = useState(false);
    const [saving, setSaving] = useState(false);
    const gridRef = useRef(null);
    const modalRef = useRef(null);

    // Draggable modal state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [isDraggingModal, setIsDraggingModal] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const dayNames = language === 'fr'
        ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const monthNames = language === 'fr'
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    // Hours to display (0h to 23h - 24 hours)
    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Get week dates based on offset
    const getWeekDates = (offset = 0) => {
        const today = new Date();
        const monday = new Date(today);
        const dayOfWeek = monday.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        monday.setDate(monday.getDate() + diffToMonday + (offset * 7));
        monday.setHours(0, 0, 0, 0);

        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            return date;
        });
    };

    const weekDates = getWeekDates(currentWeekOffset);

    // Get week range display
    const getWeekRangeDisplay = () => {
        const firstDay = weekDates[0];
        const lastDay = weekDates[6];
        const firstMonth = monthNames[firstDay.getMonth()];
        const lastMonth = monthNames[lastDay.getMonth()];
        const year = firstDay.getFullYear();

        if (firstDay.getMonth() === lastDay.getMonth()) {
            return `${firstDay.getDate()} - ${lastDay.getDate()} ${firstMonth} ${year}`;
        }
        return `${firstDay.getDate()} ${firstMonth.slice(0, 3)} - ${lastDay.getDate()} ${lastMonth.slice(0, 3)} ${year}`;
    };

    // Navigate weeks
    const goToPreviousWeek = () => setCurrentWeekOffset(prev => prev - 1);
    const goToNextWeek = () => setCurrentWeekOffset(prev => prev + 1);
    const goToCurrentWeek = () => setCurrentWeekOffset(0);

    // Reset modal position and week when opening
    useEffect(() => {
        if (isOpen) {
            setModalPosition({ x: 0, y: 0 });
            setCurrentWeekOffset(0);
        }
    }, [isOpen]);

    // Handle modal drag
    const handleModalDragStart = (e) => {
        if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT') return;
        e.preventDefault();
        setIsDraggingModal(true);
        const rect = modalRef.current?.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left - rect.width / 2,
                y: e.clientY - rect.top
            });
        }
    };

    useEffect(() => {
        if (!isDraggingModal) return;

        const handleMouseMove = (e) => {
            setModalPosition({
                x: e.clientX - window.innerWidth / 2 - dragOffset.x,
                y: e.clientY - window.innerHeight / 2 - dragOffset.y + 100
            });
        };

        const handleMouseUp = () => {
            setIsDraggingModal(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingModal, dragOffset]);

    // Get existing events for a specific day (RDV, meetings) to block
    const getBlockedSlotsForDay = (dayIndex) => {
        const dayDate = weekDates[dayIndex];
        if (!dayDate || !existingEvents || existingEvents.length === 0) return [];

        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayDate);
        dayEnd.setHours(23, 59, 59, 999);

        return existingEvents.filter(event => {
            if (event.eventType === 'availability') return false;
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            return eventStart <= dayEnd && eventEnd >= dayStart;
        }).map(event => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);

            let startHour = eventStart < dayStart ? 0 : eventStart.getHours();
            let startMinutes = eventStart < dayStart ? 0 : eventStart.getMinutes();
            let endHour = eventEnd > dayEnd ? 24 : eventEnd.getHours();
            let endMinutes = eventEnd > dayEnd ? 0 : eventEnd.getMinutes();

            // Clamp to grid range
            if (startHour < 0) startHour = 0;
            if (endHour > 24) endHour = 24;

            return {
                startHour,
                startMinutes,
                endHour,
                endMinutes,
                title: event.title,
                color: event.color || '#8b5cf6',
                // Keep original dates for display
                originalStart: eventStart,
                originalEnd: eventEnd
            };
        }).filter(slot => {
            return slot.startHour < 24 && slot.endHour > 0;
        });
    };

    // Get availability slots for a specific day
    const getAvailabilitySlotsForDay = (dayIndex) => {
        const dayDate = weekDates[dayIndex];
        if (!dayDate || !existingEvents || existingEvents.length === 0) return [];

        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayDate);
        dayEnd.setHours(23, 59, 59, 999);

        return existingEvents.filter(event => {
            if (event.eventType !== 'availability') return false;
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            return eventStart <= dayEnd && eventEnd >= dayStart;
        }).map(event => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);

            let startHour = eventStart < dayStart ? 0 : eventStart.getHours();
            let startMinutes = eventStart < dayStart ? 0 : eventStart.getMinutes();
            let endHour = eventEnd > dayEnd ? 24 : eventEnd.getHours();
            let endMinutes = eventEnd > dayEnd ? 0 : eventEnd.getMinutes();

            if (startHour < 0) startHour = 0;
            if (endHour > 24) endHour = 24;

            return {
                startHour,
                startMinutes,
                endHour,
                endMinutes,
                title: event.title,
                originalStart: eventStart,
                originalEnd: eventEnd
            };
        }).filter(slot => {
            return slot.startHour < 24 && slot.endHour > 0;
        });
    };

    // Check if a time slot overlaps with blocked slots
    const isTimeBlocked = (dayIndex, hour, minutes) => {
        const blocked = getBlockedSlotsForDay(dayIndex);
        const timeInMinutes = hour * 60 + minutes;

        return blocked.some(slot => {
            const slotStart = slot.startHour * 60 + slot.startMinutes;
            const slotEnd = slot.endHour * 60 + slot.endMinutes;
            return timeInMinutes >= slotStart && timeInMinutes < slotEnd;
        });
    };

    // Convert pixel position to time
    const getTimeFromY = (y, containerRect) => {
        const relativeY = y - containerRect.top;
        const hourHeight = containerRect.height / hours.length;
        const hourIndex = Math.floor(relativeY / hourHeight);
        const minuteOffset = ((relativeY % hourHeight) / hourHeight) * 60;
        const snappedMinutes = Math.round(minuteOffset / 15) * 15;
        const hour = Math.min(Math.max(hours[0] + hourIndex, hours[0]), hours[hours.length - 1]);
        const minutes = snappedMinutes >= 60 ? 0 : snappedMinutes;
        return { hour, minutes: minutes >= 60 ? 0 : minutes };
    };

    // Handle mouse down on grid
    const handleMouseDown = (e, dayIndex) => {
        if (e.target.closest('.slot-block') || e.target.closest('.blocked-slot')) return;

        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const time = getTimeFromY(e.clientY, rect);

        if (isTimeBlocked(dayIndex, time.hour, time.minutes)) return;

        setIsSelecting(true);
        setSelectionStart({ dayIndex, ...time });
        setCurrentSelection({
            dayIndex,
            startHour: time.hour,
            startMinutes: time.minutes,
            endHour: time.hour,
            endMinutes: time.minutes + 15
        });
    };

    // Handle mouse move during selection
    const handleMouseMove = (e) => {
        if (!isSelecting || !selectionStart) return;

        const container = gridRef.current?.querySelector(`[data-day="${selectionStart.dayIndex}"]`);
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const time = getTimeFromY(e.clientY, rect);

        const startMinutes = selectionStart.hour * 60 + selectionStart.minutes;
        let endMinutes = time.hour * 60 + time.minutes + 15;

        const maxMinutes = (hours[hours.length - 1] + 1) * 60;
        endMinutes = Math.min(endMinutes, maxMinutes);

        if (endMinutes > startMinutes) {
            setCurrentSelection({
                dayIndex: selectionStart.dayIndex,
                startHour: selectionStart.hour,
                startMinutes: selectionStart.minutes,
                endHour: Math.floor(endMinutes / 60),
                endMinutes: endMinutes % 60
            });
        }
    };

    // Handle mouse up - show mini modal
    const handleMouseUp = () => {
        if (isSelecting && currentSelection) {
            const duration = (currentSelection.endHour * 60 + currentSelection.endMinutes) -
                           (currentSelection.startHour * 60 + currentSelection.startMinutes);

            if (duration >= 15) {
                setPendingSlot(currentSelection);
                setSlotType('available');
                setSlotTitle('');
                setSlotRecurrent(false);
                setShowSlotModal(true);
                // Keep selection visible - don't clear currentSelection yet
            } else {
                setCurrentSelection(null);
            }
        }

        setIsSelecting(false);
        setSelectionStart(null);
    };

    // Format time display
    const formatTime = (hour, minutes) => {
        return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    // Calculate slot position and height
    const getSlotStyle = (slot) => {
        const startMinutes = (slot.startHour - hours[0]) * 60 + slot.startMinutes;
        const endMinutes = (slot.endHour - hours[0]) * 60 + slot.endMinutes;
        const totalMinutes = hours.length * 60;

        const top = (startMinutes / totalMinutes) * 100;
        const height = ((endMinutes - startMinutes) / totalMinutes) * 100;

        return {
            top: `${Math.max(top, 0)}%`,
            height: `${Math.max(height, 2)}%`
        };
    };

    // Get type label
    const getTypeLabel = (typeValue) => {
        const type = AVAILABILITY_TYPES.find(t => t.value === typeValue);
        return type ? (language === 'fr' ? type.labelFr : type.labelEn) : typeValue;
    };

    // Save the slot
    const handleSaveSlot = async () => {
        if (!pendingSlot) return;

        setSaving(true);
        try {
            const dayDate = weekDates[pendingSlot.dayIndex];

            const startDateTime = new Date(dayDate);
            startDateTime.setHours(pendingSlot.startHour, pendingSlot.startMinutes, 0, 0);

            const endDateTime = new Date(dayDate);
            endDateTime.setHours(pendingSlot.endHour, pendingSlot.endMinutes, 0, 0);

            const title = slotTitle.trim() || getTypeLabel(slotType);

            await onSave({
                title,
                availabilityType: slotType,
                startDateTime: startDateTime.toISOString(),
                endDateTime: endDateTime.toISOString(),
                recurrent: slotRecurrent
            });

            setShowSlotModal(false);
            setPendingSlot(null);
            setCurrentSelection(null);
        } catch (err) {
            console.error('Error saving slot:', err);
        } finally {
            setSaving(false);
        }
    };

    // Cancel slot creation
    const handleCancelSlot = () => {
        setShowSlotModal(false);
        setPendingSlot(null);
        setCurrentSelection(null);
        setSlotType('available');
        setSlotTitle('');
        setSlotRecurrent(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                style={{
                    transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                    cursor: isDraggingModal ? 'grabbing' : 'default'
                }}
            >
                {/* Header - draggable */}
                <div
                    className="flex items-center justify-between p-4 border-b border-gray-200 bg-green-50 cursor-grab active:cursor-grabbing"
                    onMouseDown={handleModalDragStart}
                >
                    <div className="flex items-center gap-3">
                        <GripHorizontal className="w-5 h-5 text-gray-400" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">
                                {language === 'fr' ? 'Configurer les disponibilités' : 'Configure Availability'}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {language === 'fr'
                                    ? 'Cliquez et glissez pour créer des créneaux'
                                    : 'Click and drag to create slots'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Week navigation */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <button
                        onClick={goToPreviousWeek}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>

                    <div className="flex items-center gap-3">
                        <h4 className="text-base font-medium text-gray-800">
                            {getWeekRangeDisplay()}
                        </h4>
                        {currentWeekOffset !== 0 && (
                            <button
                                onClick={goToCurrentWeek}
                                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                            >
                                {language === 'fr' ? 'Aujourd\'hui' : 'Today'}
                            </button>
                        )}
                    </div>

                    <button
                        onClick={goToNextWeek}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Grid */}
                <div
                    ref={gridRef}
                    className="flex-1 overflow-auto p-4"
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <div className="flex min-w-[700px]">
                        {/* Hour labels */}
                        <div className="w-14 flex-shrink-0">
                            <div className="h-12" />
                            {hours.map(hour => (
                                <div key={hour} className="h-12 text-xs text-gray-400 text-right pr-2 -mt-2">
                                    {hour}:00
                                </div>
                            ))}
                        </div>

                        {/* Days grid */}
                        <div className="flex-1 grid grid-cols-7 gap-1">
                            {dayNames.map((day, dayIndex) => {
                                const dayDate = weekDates[dayIndex];
                                const blockedSlots = getBlockedSlotsForDay(dayIndex);
                                const isToday = dayDate?.toDateString() === new Date().toDateString();

                                return (
                                    <div key={day} className="flex flex-col">
                                        {/* Day header */}
                                        <div className={`h-12 flex flex-col items-center justify-center border-b border-gray-200 rounded-t ${isToday ? 'bg-green-100' : 'bg-gray-50'}`}>
                                            <span className={`text-sm font-medium ${isToday ? 'text-green-700' : 'text-gray-700'}`}>{day}</span>
                                            <span className={`text-xs ${isToday ? 'text-green-600' : 'text-gray-400'}`}>
                                                {dayDate?.getDate()}/{(dayDate?.getMonth() + 1)}
                                            </span>
                                        </div>

                                        {/* Day column */}
                                        <div
                                            data-day={dayIndex}
                                            className="relative bg-white border border-gray-200 rounded-b cursor-crosshair"
                                            style={{ height: `${hours.length * 48}px` }}
                                            onMouseDown={(e) => handleMouseDown(e, dayIndex)}
                                        >
                                            {/* Hour lines */}
                                            {hours.map((hour, idx) => (
                                                <div
                                                    key={hour}
                                                    className="absolute w-full border-t border-gray-100"
                                                    style={{ top: `${(idx / hours.length) * 100}%` }}
                                                />
                                            ))}

                                            {/* Blocked slots (existing RDV/meetings) - gray */}
                                            {blockedSlots.map((slot, idx) => {
                                                const isSameDay = slot.originalStart.toDateString() === slot.originalEnd.toDateString();
                                                const startTime = slot.originalStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const endTime = slot.originalEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const startDateStr = slot.originalStart.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                                const endDateStr = slot.originalEnd.toLocaleDateString([], { day: '2-digit', month: '2-digit' });

                                                const timeDisplay = isSameDay
                                                    ? `${startTime} - ${endTime}`
                                                    : `${startDateStr} ${startTime} - ${endDateStr} ${endTime}`;

                                                return (
                                                    <div
                                                        key={`blocked-${idx}`}
                                                        className="blocked-slot absolute left-0.5 right-0.5 rounded cursor-not-allowed overflow-hidden bg-gray-300"
                                                        style={getSlotStyle(slot)}
                                                        title={`${slot.title} (${language === 'fr' ? 'Non disponible' : 'Not available'})`}
                                                    >
                                                        <div className="h-full w-full p-1 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(0,0,0,0.1)_4px,rgba(0,0,0,0.1)_8px)]">
                                                            <div className="flex items-center gap-1">
                                                                <div
                                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                                    style={{ backgroundColor: slot.color }}
                                                                />
                                                                <span className="text-[10px] text-gray-600 font-medium truncate">
                                                                    {slot.title}
                                                                </span>
                                                            </div>
                                                            <span className="text-[9px] text-gray-500">
                                                                {timeDisplay}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Saved availability slots - green with dashed border */}
                                            {getAvailabilitySlotsForDay(dayIndex).map((slot, idx) => {
                                                const isSameDay = slot.originalStart.toDateString() === slot.originalEnd.toDateString();
                                                const startTime = slot.originalStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const endTime = slot.originalEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const startDateStr = slot.originalStart.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                                const endDateStr = slot.originalEnd.toLocaleDateString([], { day: '2-digit', month: '2-digit' });

                                                const timeDisplay = isSameDay
                                                    ? `${startTime} - ${endTime}`
                                                    : `${startDateStr} ${startTime} - ${endDateStr} ${endTime}`;

                                                return (
                                                    <div
                                                        key={`avail-${idx}`}
                                                        className="slot-block absolute left-0.5 right-0.5 rounded-md border-2 border-dashed border-green-600 bg-green-400/50 overflow-hidden"
                                                        style={getSlotStyle(slot)}
                                                    >
                                                        <div className="h-full w-full p-1">
                                                            <span className="text-[10px] text-green-800 font-medium truncate block">
                                                                {slot.title}
                                                            </span>
                                                            <span className="text-[9px] text-green-700">
                                                                {timeDisplay}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Current selection preview with dashed border */}
                                            {(isSelecting || showSlotModal) && currentSelection && currentSelection.dayIndex === dayIndex && (
                                                <div
                                                    className="absolute left-1 right-1 bg-green-400/50 rounded-md border-2 border-dashed border-green-600 text-green-800 flex flex-col items-center justify-center"
                                                    style={getSlotStyle(currentSelection)}
                                                >
                                                    <span className="text-sm font-bold">
                                                        {formatTime(currentSelection.startHour, currentSelection.startMinutes)}
                                                    </span>
                                                    <span className="text-xs">↓</span>
                                                    <span className="text-sm font-bold">
                                                        {formatTime(currentSelection.endHour, currentSelection.endMinutes)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-green-500 rounded" />
                                <span>{language === 'fr' ? 'Disponibilité' : 'Availability'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-gray-300 rounded" style={{ background: 'repeating-linear-gradient(45deg, #d1d5db, #d1d5db 2px, #e5e7eb 2px, #e5e7eb 4px)' }} />
                                <span>{language === 'fr' ? 'Non disponible' : 'Not available'}</span>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            {language === 'fr' ? 'Fermer' : 'Close'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mini modal for slot details */}
            {showSlotModal && pendingSlot && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-4 mx-4">
                        {/* Color bar */}
                        <div className="h-2 -mx-4 -mt-4 mb-4 bg-green-500 rounded-t-xl" />

                        <h4 className="text-lg font-semibold text-gray-800 mb-1">
                            {language === 'fr' ? 'Nouvelle disponibilité' : 'New availability'}
                        </h4>

                        {/* Time display */}
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                            <Clock className="w-4 h-4" />
                            <span>
                                {dayNames[pendingSlot.dayIndex]} {weekDates[pendingSlot.dayIndex]?.getDate()}/{weekDates[pendingSlot.dayIndex]?.getMonth() + 1}
                            </span>
                            <span className="font-medium">
                                {formatTime(pendingSlot.startHour, pendingSlot.startMinutes)} - {formatTime(pendingSlot.endHour, pendingSlot.endMinutes)}
                            </span>
                        </div>

                        {/* Type selector */}
                        <div className="mb-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                                {language === 'fr' ? 'Type de disponibilité' : 'Availability type'}
                            </label>
                            <CustomDropdown
                                value={slotType}
                                onChange={(val) => setSlotType(val)}
                                options={AVAILABILITY_TYPES.map(type => ({
                                    value: type.value,
                                    label: language === 'fr' ? type.labelFr : type.labelEn
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-green-500 focus:ring-2 focus:ring-green-500"
                            />
                        </div>

                        {/* Custom title input */}
                        <input
                            type="text"
                            placeholder={language === 'fr' ? 'Titre personnalisé (optionnel)' : 'Custom title (optional)'}
                            value={slotTitle}
                            onChange={(e) => setSlotTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />

                        {/* Recurrence checkbox */}
                        <label className="flex items-center gap-2 mb-4 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={slotRecurrent}
                                onChange={(e) => setSlotRecurrent(e.target.checked)}
                                className="w-4 h-4 text-green-600 rounded"
                            />
                            <Repeat className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                                {language === 'fr' ? 'Répéter chaque semaine' : 'Repeat weekly'}
                            </span>
                        </label>

                        {/* Actions */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleCancelSlot}
                                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                            </button>
                            <button
                                onClick={handleSaveSlot}
                                disabled={saving}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                                {saving ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                {language === 'fr' ? 'Enregistrer' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvailabilityModal;
